/**
 * The walk both tree components draw from (`KLQ-20260905-002` §B).
 *
 * It was private to `TreeTable.tsx` until `TreePicker` needed the same flattening for the
 * same reason — one traversal, one set of rules about depth and group rows. Moved here
 * unchanged rather than reimplemented: a second walk is how two surfaces that claim to be
 * the same instrument start disagreeing about what a collapsed branch is.
 *
 * Not exported from `components/index.ts`. It is the tree-table folder's own shared walk,
 * not a primitive a screen has any business calling.
 */

import type { TreeTableMode, TreeTableNode } from "./TreeTable";

/**
 * Which ids start closed, for a tree nobody has touched yet.
 *
 * **A tree that opens fully is a tree that renders every node it was handed** — which is the
 * whole cost `KLQ-20260825-008` §5.1 sets out to bound, and it does not care that the nodes
 * were cheap to fetch. `collapsed` is a set of *closed* ids, so its empty value means "all
 * open"; that was the default until 2026-09-10 and it is the reason a twelve-thousand-node
 * project put twelve thousand rows in the DOM.
 *
 * `openToDepth` counts levels that stay open, so `1` draws the root and its children and closes
 * everything under them. A node with no children is never added: closing a leaf would draw a
 * twisty that does nothing.
 *
 * Derived from the nodes rather than seeded into state, so it follows the data as it arrives
 * and a reader's own first press replaces it wholesale — see `TreeTable`'s `collapsed`.
 */
export function collapsedByDefault<
  Node extends {
    readonly id: string;
    readonly expandable?: boolean | undefined;
    readonly children?: readonly Node[] | undefined;
  },
>(
  // Structural rather than `TreeTableNode<Row>` since 2026-09-10: the slicer rail's options
  // are the same shape as far as this rule is concerned — an id, maybe children — and the
  // default a person meets in the rail has to be the default they meet in the table. A copy
  // of the arithmetic per tree is how two trees start disagreeing about what starts open.
  nodes: readonly Node[],
  openToDepth: number,
  depth = 0,
): Set<string> {
  const closed = new Set<string>();
  for (const node of nodes) {
    if (!expandable(node)) continue;
    if (depth >= openToDepth) closed.add(node.id);
    for (const id of collapsedByDefault(node.children ?? [], openToDepth, depth + 1))
      closed.add(id);
  }
  return closed;
}

/**
 * Whether this row has a twisty — **declared, or inferred from the children it has**.
 *
 * The inference was the whole rule until 2026-09-10, and it stops being enough the moment a
 * branch's contents are fetched when the branch is opened (`KLQ-20260825-008` §5.1): a floor
 * whose drawings have not been asked for yet has no children, reads as a leaf, offers no
 * twisty, and so is never opened — which is the only event that would have fetched them. A
 * consumer that fetches lazily says `expandable: true` on the row instead, and the tree stops
 * needing the answer to be already in its hands.
 *
 * Still one function, read by the walk, by the default-collapse and by the keyboard binding,
 * so those three cannot disagree about what has a twisty.
 */
export function expandable<
  Node extends {
    readonly expandable?: boolean | undefined;
    readonly children?: readonly Node[] | undefined;
  },
>(node: Node): boolean {
  return node.expandable ?? (node.children ?? []).length > 0;
}

/** One rendered line: a node, flattened, with the depth and the state the row needs. */
export interface Line<Row> {
  readonly id: string;
  readonly row: Row;
  readonly depth: number;
  readonly group: boolean;
  /** A sentence about the list rather than a row of it — see `TreeTableNode.notice`. */
  readonly notice: boolean;
  readonly expandable: boolean;
  readonly open: boolean;
}

/**
 * The tree, as the lines actually drawn.
 *
 * Flat mode is the same walk with the depth pinned to zero and the group rows dropped — one
 * traversal, one set of rules, which is rule 1 in the only place it could be broken.
 */
export function lines<Row>(
  nodes: readonly TreeTableNode<Row>[],
  mode: TreeTableMode,
  collapsed: ReadonlySet<string>,
  depth = 0,
): Line<Row>[] {
  const out: Line<Row>[] = [];
  for (const node of nodes) {
    const children = node.children ?? [];
    const group = node.group ?? false;
    const notice = node.notice ?? false;
    if (mode === "flat") {
      if (!group)
        out.push({
          id: node.id,
          row: node.row,
          depth: 0,
          group,
          notice,
          expandable: false,
          open: false,
        });
      out.push(...lines(children, mode, collapsed, 0));
      continue;
    }
    const twisty = expandable(node);
    const open = twisty && !collapsed.has(node.id);
    out.push({
      id: node.id,
      row: node.row,
      depth,
      group,
      notice,
      expandable: twisty,
      open,
    });
    if (open) out.push(...lines(children, mode, collapsed, depth + 1));
  }
  return out;
}
